import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "./Home.css";

const proofPoints = [
  "Ape language research spanning decades",
  "Hundreds of publications and documentary references",
  "A restored live lexigram keyboard for public learning",
  "Work led by noted primatologist Dr. Sue Savage-Rumbaugh",
];

const featureLinks = [
  {
    title: "Try the Bonobo Hope Keyboard",
    text: "Explore the lexigram system and hear the words spoken aloud.",
    link: "/keyboard",
    image: "/KeyboardP1_6.png",
    cta: "Open keyboard",
    kicker: "01",
  },
  {
    title: "Watch the archive",
    text: "See video material that documents the evidence, the methods, and the original research environment.",
    link: "/video",
    image: "/KanziSlideshow/KanziPointsToLexigram.jpg",
    cta: "Watch videos",
    kicker: "02",
  },
  {
    title: "Read the research",
    text: "Browse selected publications from the foundations of ape language study.",
    link: "/SueBiblio",
    image: "/sue-savage-rumbaugh.jpg",
    cta: "View publications",
    kicker: "03",
  },
];

const kanziImages = [
  {
    src: "/KanziSlideshow/BabyKanzi.jpg",
    alt: "Kanzi as a young bonobo",
    caption: "Early life",
  },
  {
    src: "/KanziSlideshow/KanziAndKeyboard2.jpg",
    alt: "Kanzi beside a lexigram keyboard",
    caption: "Lexigram research",
  },
  {
    src: "/KanziSlideshow/KanziAndSue.jpg",
    alt: "Kanzi with Sue Savage-Rumbaugh",
    caption: "Human-bonobo relationship",
  },
  {
    src: "/KanziSlideshow/KanziPointsToLexigram.jpg",
    alt: "Kanzi pointing toward a lexigram",
    caption: "Symbolic communication",
  },
];

const setMetaTag = (selector, attributes) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    document.head.appendChild(tag);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    tag.setAttribute(key, value);
  });
};

const Home = () => {
  useEffect(() => {
    document.title =
      "Bonobo Hope Initiative | Restore Bonobo Language Research";

    setMetaTag('meta[name="description"]', {
      name: "description",
      content:
        "Help restore and continue the bonobo and chimpanzee language research trajectory built around lexigrams, culture, and interspecies communication.",
    });
    setMetaTag('meta[property="og:title"]', {
      property: "og:title",
      content: "Help Restore Bonobo and Chimpanzee Language Research",
    });
    setMetaTag('meta[property="og:description"]', {
      property: "og:description",
      content:
        "Support the restoration of lexigram-based ape language research, public education, archive work, and bonobo-centered advocacy.",
    });
    setMetaTag('meta[property="og:image"]', {
      property: "og:image",
      content: "/KanziSlideshow/KanziAndKeyboard2.jpg",
    });

    const schemaId = "bhi-home-schema";
    let schema = document.getElementById(schemaId);
    if (!schema) {
      schema = document.createElement("script");
      schema.id = schemaId;
      schema.type = "application/ld+json";
      document.head.appendChild(schema);
    }
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "NGO",
      name: "Bonobo Hope Initiative",
      url: window.location.origin,
      logo: `${window.location.origin}/bonobo-hope-logo.png`,
      description:
        "Bonobo Hope Initiative works to restore and continue lexigram-based bonobo and chimpanzee language research while supporting education, conservation, and bonobo-centered care.",
      sameAs: [],
    });
  }, []);

  return (
    <main className="home-page">
      <section className="home-hero" aria-labelledby="home-hero-title">
        <div className="home-hero__content">
          <p className="home-eyebrow">Bonobo language, dignity, and research</p>
          <h1 id="home-hero-title">
            Help restore the research that gave bonobos a voice.
          </h1>
          <p className="home-hero__lead">
            Bonobo Hope Initiative is working to revive the original research
            trajectory that enabled bonobos and chimpanzees to communicate with
            humans through lexigrams, shared culture, and sustained
            relationships.
          </p>
          <div className="home-hero__actions" aria-label="Primary actions">
            <Link className="home-button home-button--primary" to="/donate">
              Donate now
            </Link>
            <Link className="home-button home-button--secondary" to="/video">
              Explore video archive
            </Link>
          </div>
          <div className="home-hero__metrics" aria-label="Key impact signals">
            <div>
              <strong>40+</strong>
              <span>years of language research</span>
            </div>
            <div>
              <strong>1980-2025</strong>
              <span>Kanzi's historic life</span>
            </div>
          </div>
        </div>

        <div className="home-hero__media" aria-label="Kanzi and lexigram work">
          <img
            className="home-hero__image"
            src="/KanziSlideshow/KanziAndKeyboard2.jpg"
            alt="Kanzi beside a lexigram keyboard"
          />
          <div className="home-hero__media-note">
            <span>Featured archive</span>
            <strong>Kanzi and the lexigram keyboard</strong>
          </div>
        </div>
      </section>

      <section className="home-proof" aria-label="Credibility highlights">
        <p className="home-proof__label">Research foundation</p>
        <div className="home-proof__track">
          {proofPoints.map((point) => (
            <span key={point}>{point}</span>
          ))}
        </div>
      </section>

      <div className="home-continuum">
        <div className="home-continuum__inner">
          <section
            className="home-story home-section"
            aria-labelledby="story-title"
          >
            <div className="home-story__headline">
              <p className="home-section__label">Why this work matters</p>
              <h2 id="story-title">
                Bonobos changed what science could imagine about language.
              </h2>
            </div>
            <div className="home-story__body">
              <p>
                <Link to="/research/kanzi">Kanzi</Link> and his family helped
                reveal that symbolic understanding, cultural learning, emotional
                nuance, and shared attention are not exclusively human
                achievements. Their story still matters because it challenges
                how we educate, conserve, and care.
              </p>
              <p>
                Today, the task is not only to remember what happened. It is to
                rebuild the conditions that made the work possible: long-term
                trust, language-rich environments, serious scientific attention,
                restored <Link to="/keyboard">lexigram tools</Link>, and public
                support for <Link to="/research/bonobos">bonobos</Link> and
                chimpanzees as communicative beings.
              </p>
            </div>
          </section>

          <section
            className="home-memorial home-section"
            aria-labelledby="memorial-title"
          >
            <div className="home-memorial__slideshow" aria-hidden="true">
              {kanziImages.map((image, index) => (
                <img
                  key={image.src}
                  src={image.src}
                  alt=""
                  style={{ animationDelay: `${index * 5}s` }}
                />
              ))}
            </div>
            <div className="home-memorial__panel">
              <div className="home-section__intro">
                <p className="home-section__label">
                  Celebrating the life of Kanzi
                </p>
                <h2 id="memorial-title">
                  Kanzi's voice changed the human story.
                </h2>
                <p>
                  Kanzi (1980-2025) helped generations of researchers, students,
                  and visitors understand bonobos as thinking, feeling,
                  culturally responsive beings with profound communicative
                  abilities.
                </p>
              </div>
              <details className="home-memorial__details">
                <summary>Continue reading</summary>
                <div className="home-memorial__text">
              <p>
                Quite suddenly, Kanzi (1980-2025) passed away on March 18
                <sup>th</sup> at the <i>Ape Initiative</i> Lab in Des Moines,
                IA. We view this as an irreparable loss for science and for
                humanity. In acquiring at an early age an astounding level of
                comprehension of spoken English as well as a very high command
                of arbitrary signs (called lexigrams) through which he could
                communicate with humans, bonobo Kanzi led the way in the
                redefinition of the human mind, the role of culture, the
                function of a symbolic environment. Kanzi learned to make stone
                tools and fire, he understood narratives and movies, he could
                produce art and make jokes. He was the object of several
                documentary movies and of hundreds of publications. For more than
                a decade now, Dr. Sue Savage-Rumbaugh, the experimental
                psychologist and primatologist who raised Kanzi and presented
                his achievements to the scientific community and the larger
                public as early as the 1990s, was unduly prevented from getting
                access to him and the rest of the group. Bonobo Hope
                International (BHI), legally the "co-owner" of Kanzi and of
                several other members of the family living in Des Moines, has
                been similarly set aside by the <i>Ape Initiative.</i> BHI
                laments Kanzi's passing.
              </p>
              <p>
                BHI deplores the long-term separation of the human and bonobo
                families whose lives once became enmeshed. BHI deeply regrets
                that Kanzi's unique intellectual and personal capabilities could
                not be shown to the outside world in their proper light since
                Savage-Rumbaugh's removal in 2013. Through the crude downsizing
                of Kanzi's cultural environment since that period, Kanzi's voice
                has largely been silenced, many years before his tragic death.
                But his legacy continues, as will be soon be seen in further
                publications, exhibitions, and other initiatives. Moreover,
                Kanzi's relatives Nyota and Teco, who also have access to a
                reality mediated by human culture and language, continue to have
                many things to teach us, as do the other bonobos in Des Moines
                and elsewhere.
              </p>
              <p>
                BHI is inviting all those who feel the pain of Kanzi's passing
                to ask for the restoration of a research trajectory that will
                focus on the dignity of the bonobo group. "Right now" (as Kanzi
                would often say with some sense of urgency) is the time for
                uniting and recreating the conditions of life and research that
                would be in phase with the cognitive potentiality and emotional
                needs of Nyota, Teco, and their friends and relatives. Dr.
                Savage-Rumbaugh should be given back access to the bonobo group.
                The language research should finally be restored in its full
                form. BHI wishes to lead that effort and help the Ape Initiative
                to reach that scholarly and moral goal.
              </p>
                </div>
              </details>
            </div>
          </section>

          <section
            className="home-paper home-section"
            aria-labelledby="paper-title"
          >
            <div className="home-paper__media">
              <img
                src="/pan-survival.png"
                alt="Pan Survival in the 21st Century cover artwork"
              />
            </div>
            <div className="home-paper__content">
              <p className="home-section__label">Featured paper</p>
              <h2 id="paper-title">Pan Survival in the 21st Century</h2>
              <p>
                Chimpanzees (<i>Pan troglodytes</i>) and bonobos (
                <i>Pan paniscus</i>) should be added to the <i>Homo</i> genus
                and given the right to freedom and independence, argue
                evolutionary anthropologist Itai Roffman and his colleagues.
              </p>
              <a
                className="home-button home-button--secondary"
                href="http://www.pontecorboli.com/digital/OpenAccess/Roffman_Pan.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Read the paper
              </a>
            </div>
          </section>

          <section
            className="home-feature home-section"
            aria-labelledby="explore-title"
          >
            <div className="home-section__intro">
              <p className="home-section__label">Explore the work</p>
              <h2 id="explore-title">
                Learn the language, watch the evidence, read the research.
              </h2>
              <p>
                The original research trajectory was built through immersion,
                interaction, and symbols that carried meaning across species.
                Start with the keyboard, the videos, and the publications that
                show how the work developed, then move deeper into the{" "}
                <Link to="/research">research trajectory</Link>.
              </p>
            </div>
            <div className="home-feature__pathway">
              {featureLinks.map((item) => (
                <article className="home-feature-step" key={item.title}>
                  <div className="home-feature-step__media">
                    <img src={item.image} alt="" />
                  </div>
                  <div className="home-feature-step__content">
                    <span>{item.kicker}</span>
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                    <Link to={item.link}>{item.cta}</Link>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>
      </div>

      <section
        className="home-donate"
        aria-labelledby="donate-title"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(24, 75, 61, 0.96), rgba(13, 111, 112, 0.94)), url('/KanziSlideshow/KanziFloorLexigrams.jpg')",
        }}
      >
        <div>
          <p className="home-section__label">Support BHI</p>
          <h2 id="donate-title">Help restart the work.</h2>
          <p>
            Help restore the scientific, educational, and ethical conditions
            that made lexigram-based communication research possible.
          </p>
        </div>
        <Link className="home-button home-button--light" to="/donate">
          Support the work
        </Link>
      </section>
    </main>
  );
};

export default Home;
